import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { stageClass, URGENCY_STYLES } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Inbox, FileText, ClipboardCheck, ShieldAlert, CheckCircle, AlarmClock, PauseCircle, Ban, ExternalLink, PhoneCall, XCircle, Send, CalendarCheck, UserMinus, MapPin, Droplet, Sun, Coffee, Smile, Heart, Sparkles as SparkIcon } from 'lucide-react';
import ClientFormDialog from '../components/ClientFormDialog';
import { fmtDate } from '../lib/date';

// Empty-tile message library (iter 48). One message + matching icon is
// picked at random per empty section so an all-clear state feels warm
// instead of a hole in the layout. Kept short, on-brand and never cheesy.
const EMPTY_MESSAGES = [
  { icon: Smile,     text: 'All caught up here.' },
  { icon: Droplet,   text: 'All caught up. Hey — did you hydrate today?' },
  { icon: Coffee,    text: 'Clean slate. Grab a coffee refill?' },
  { icon: Sun,       text: 'Nothing pending. Take a two-minute stretch.' },
  { icon: Heart,     text: 'Zero items. Your team is on it.' },
  { icon: CheckCircle, text: 'Empty queue — nice work.' },
  { icon: SparkIcon,text: 'Quiet on this front. Deep breath.' },
];

// Buckets sourced from `inquiries` collection don't have client rows in the
// pipeline table — they route to /inquiries triage. So no "View all in
// Pipeline" link for those tiles.
const isInquiryBucket = (s) => s?.sourceCollection === 'inquiries';

// Deep-link target section for each bucket's Quick Action button (iter 47).
// The ClientFormDialog auto-scrolls to `[data-section=<key>]` on open.
const SECTION_FOCUS = {
  new_referrals: 'paperwork',
  paperwork_pending: 'paperwork',
  paperwork_followup: 'paperwork',
  paperwork_stalled: 'paperwork',
  ready_for_eval: 'evaluation',
  eval_scheduled: 'evaluation',
  eval_overdue: 'evaluation',
  ready_for_therapy: 'therapy',
  ready_to_submit_preauth: 'preauth',
  pre_auth_pending: 'preauth',
  waitlisted: 'therapy',
  on_hold: 'hold',
};

// Weekday + MM-DD-YYYY header — keeps the "which day are we on" cue but
// aligns the numeric part with the team's app-wide MM-DD-YYYY format.
function fmtToday() {
  const now = new Date();
  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' });
  return `${weekday} · ${fmtDate(now)}`;
}
import { toast } from 'sonner';

const DISPOSITION_OUTCOMES = ['Lost', 'Withdrew', 'Not Eligible', 'Financial/Ins Issue', 'Discharged'];

function DisposeButton({ client, onDone }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState('Lost');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error('Reason is required for the audit trail');
    setSaving(true);
    try {
      await api.post(`/clients/${client.client_id}/dispose`, { outcome, reason: reason.trim() });
      toast.success(`Marked ${client.client_name} as ${outcome}`);
      setOpen(false);
      setReason('');
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disposition failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-[11px] px-2 py-1 rounded-full bg-[#8A3A3A]/15 hover:bg-[#8A3A3A]/25 text-[#8A3A3A] font-semibold border border-[#8A3A3A]/30 flex items-center gap-1 shrink-0"
        data-testid={`dispose-btn-${client.client_id}`}
        title="Mark Lost / Withdrew"
      >
        <UserMinus size={11} /> Dispose
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="dispose-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Close the loop on {client.client_name}</DialogTitle>
            <DialogDescription className="text-xs text-[#685B72] mt-1">
              3+ paperwork follow-ups have been made without response. Record the disposition so the client leaves the active pipeline. This action is fully audited.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#685B72]">Outcome</label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="dispose-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISPOSITION_OUTCOMES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#685B72]">Reason <span className="text-[#8A3A3A]">*</span></label>
              <Textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. Family unresponsive after 3 follow-ups over 2 weeks; last contact 2026-07-10."
                className="rounded-xl mt-1"
                data-testid="dispose-reason"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl" data-testid="dispose-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#8A3A3A] hover:bg-[#8A3A3A] text-white rounded-xl" data-testid="dispose-confirm">
              {saving ? 'Saving…' : `Mark ${outcome}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const SECTIONS = [
  { key: 'new_online_inquiries',    label: 'New Online Inquiries',     desc: 'Website submissions awaiting triage — review + convert to Client (or dispose)', owner: 'Admin', icon: Inbox,          prominent: true, sourceCollection: 'inquiries' },
  { key: 'inquiries_needs_followup', label: 'Contacted · Needs Follow-up', desc: 'Reached out 1+ business day ago with no reply — try them again', owner: 'Admin', icon: PhoneCall, prominent: true, sourceCollection: 'inquiries' },
  { key: 'new_referrals',           label: 'New Referrals',            desc: 'Client Pipeline: paperwork must go out within 1 business day',    owner: 'Admin',           icon: Inbox,          pipelineStage: 'Referral Received' },
  { key: 'paperwork_pending',       label: 'Paperwork Pending',        desc: 'Paperwork sent, waiting on family (<2 bd)',                      owner: 'Admin',           icon: FileText,       pipelineStage: 'Paperwork Pending' },
  { key: 'paperwork_followup',      label: 'Paperwork Follow-up',      desc: 'Sent 2+ business days ago — chase family (up to 3 follow-ups)',  owner: 'Admin',           icon: PhoneCall,      pipelineStage: 'Paperwork Pending' },
  { key: 'paperwork_stalled',       label: 'Paperwork Stalled',        desc: '3+ follow-ups made, still no paperwork — mark Lost / Withdrew',  owner: 'Admin',           icon: XCircle,        prominent: true, pipelineStage: 'Paperwork Pending' },
  { key: 'ready_for_eval',          label: 'Ready to Schedule Eval',   desc: 'Paperwork complete — schedule the eval NOW',                     owner: 'Admin',           icon: ClipboardCheck, pipelineStage: 'Ready for Eval' },
  { key: 'eval_scheduled',          label: 'Eval Scheduled',           desc: 'Eval is booked — conduct on scheduled date',                     owner: 'Admin',           icon: CalendarCheck,  pipelineStage: 'Eval Scheduled', clinicOnly: true },
  { key: 'eval_overdue',            label: 'Eval Overdue',             desc: 'Eval scheduled 2+ business days ago, still no completion',       owner: 'Office Director', icon: AlarmClock,     prominent: true, pipelineStage: 'Eval Scheduled' },
  { key: 'ready_to_submit_preauth', label: 'Submit Pre-Auth',          desc: 'Eval complete, pre-auth required — submit request to insurance', owner: 'Admin',           icon: Send,           prominent: true, pipelineStage: 'Pre-Auth Pending' },
  { key: 'pre_auth_pending',        label: 'Pre-Auth Pending',         desc: 'Submitted — waiting on insurance response',                      owner: 'Admin',           icon: ShieldAlert,    pipelineStage: 'Pre-Auth Pending' },
  { key: 'ready_for_therapy',       label: 'Ready for Therapy',        desc: 'Eval completed, insurance cleared — assign a therapist and set a start date',   owner: 'Office Director', icon: CheckCircle,    pipelineStage: 'Ready for Therapy' },
  { key: 'waitlisted',              label: 'Waitlisted',               desc: 'Standing queue — pull when capacity opens',                      owner: 'Office Director', icon: PauseCircle,    pipelineStage: 'Waitlisted' },
  { key: 'on_hold',                 label: 'On Hold',                  desc: 'Review whether the hold is still valid',                         owner: 'Admin',           icon: Ban,            prominent: true, pipelineStage: 'On Hold' },
];

function UrgencyBadge({ urgency }) {
  const cls = URGENCY_STYLES[urgency] || URGENCY_STYLES.LOW;
  return <span className={`stage-badge border text-[10px] ${cls}`}>{urgency}</span>;
}

function DaysBadge({ days, showZero }) {
  if (days === 0 && !showZero) return null;
  return <span className="text-xs font-mono text-[#685B72]">{days}bd</span>;
}

// Deterministic per-section pick — the section key hashes into an index
// so the same section always shows the same encouragement across
// re-renders (no flicker on data reload) but different sections vary.
function _hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function EmptyTile({ sectionKey }) {
  const { user } = useAuth();
  const first = (user?.name || '').split(' ')[0];
  const pool = useMemo(() => {
    const base = [...EMPTY_MESSAGES];
    // Sprinkle a personalized greeting into the pool ~once per session.
    if (first) {
      base.push({ icon: Smile, text: `How is your day going, ${first}?` });
      base.push({ icon: Sun,   text: `Nothing pending on this one, ${first}.` });
    }
    return base;
  }, [first]);
  const pick = pool[_hash(sectionKey + (first || '')) % pool.length];
  const Icon = pick.icon;
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center text-[#685B72] gap-3 py-6" data-testid={`empty-${sectionKey}`}>
      <div
        className="rounded-full p-3.5 backdrop-blur-sm ring-1 ring-[#BAA9CA]/25"
        style={{ background: 'radial-gradient(closest-side, rgba(186,169,202,0.28), rgba(186,169,202,0.10) 70%, transparent)' }}
      >
        <Icon size={22} className="text-[#685B72]" strokeWidth={1.6} />
      </div>
      <div
        className="text-base text-[#685B72] max-w-[240px] leading-snug italic"
        style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
      >
        {pick.text}
      </div>
    </div>
  );
}

export default function TodaysActions({ scopedClinic = null, hideSectionKeys = [] }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [setup, setSetup] = useState({});
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Focus target for the client modal (iter 47 quick-action deep-link).
  // The modal watches this prop and scrolls to the matching data-section.
  const [focusSection, setFocusSection] = useState('');

  const load = useCallback(() => {
    const params = scopedClinic ? { clinic: scopedClinic } : {};
    api.get('/dashboard/today', { params }).then(r => setData(r.data));
  }, [scopedClinic]);
  useEffect(() => { load(); api.get('/setup').then(r => setSetup(r.data || {})); }, [load]);

  // Hash-scroll & highlight (iter 61): Dashboard tiles deep-link here via
  // /today#<section_key>. When the section is in view, briefly ring it so
  // the user's eye lands where the count came from. Waits for `data` to be
  // present so the section actually exists in the DOM.
  useEffect(() => {
    if (!data) return;
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-[#8A3A3A]/40', 'transition-shadow');
      setTimeout(() => el.classList.remove('ring-2', 'ring-[#8A3A3A]/40'), 2000);
    }, 100);
  }, [data]);

  const openClient = async (id, section = '') => {
    const r = await api.get(`/clients/${id}`);
    setSelected(r.data);
    setFocusSection(section);
    setDialogOpen(true);
  };

  const saveClient = async (payload) => {
    try {
      const r = await api.patch(`/clients/${selected.client_id}`, payload);
      setSelected(r.data);
      setDialogOpen(false);
      toast.success('Client updated');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const deleteClient = async (c) => {
    try {
      await api.delete(`/clients/${c.client_id}`);
      setDialogOpen(false);
      toast.success(`${c.client_name || 'Client'} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  if (!data) return <div className="text-[#685B72]" data-testid="today-loading">Loading…</div>;

  const visibleSections = SECTIONS.filter(s => {
    if (hideSectionKeys.includes(s.key)) return false;
    // clinicOnly tiles (e.g. Eval Scheduled) only render on per-clinic tabs.
    if (s.clinicOnly && !scopedClinic) return false;
    // OD role (iter 46+47+55): ODs only see OD-owned tiles + On Hold + the
    // clinicOnly Eval Scheduled tile (surfaces on clinic tabs so OD knows
    // who's on the schedule today). NEW: ODs also see New Online Inquiries
    // scoped to their clinic — the backend already returns the correct
    // clinic-filtered list so they can watch triage progress even though
    // admins own conversion.
    if (user?.role === 'office_director'
        && s.owner !== 'Office Director'
        && s.key !== 'on_hold'
        && s.key !== 'eval_scheduled'
        && s.key !== 'new_online_inquiries'
        && s.key !== 'inquiries_needs_followup') return false;
    return true;
  });
  const total = visibleSections.reduce((s, sec) => s + (data[sec.key]?.length || 0), 0);

  return (
    <div className="space-y-6" data-testid="today-page">
      {!scopedClinic && (
        <div>
          <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Daily briefing</div>
          <h1 className="font-serif text-4xl text-[#2D2833] mt-1">Today's Actions</h1>
          <p className="text-[#685B72] mt-1">{total} open actions across the org · {fmtToday()}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {visibleSections.map(s => {
          const items = data[s.key] || [];
          const Icon = s.icon;
          const isProminent = s.prominent && items.length > 0;
          const cardTone = isProminent ? 'border-[#8A3A3A]/50 ring-2 ring-[#8A3A3A]/20' : 'border-white/60';
          // Premium tile treatment: soft multi-layered shadow, generous
          // padding, backdrop blur so the warm background reads through.
          const cardCls = `rounded-2xl p-7 flex flex-col overflow-hidden h-[340px] min-h-[340px] backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 ${cardTone}`;
          const cardStyle = {
            background: isProminent
              ? 'linear-gradient(180deg, rgba(255,255,255,0.85), rgba(138,58,58,0.06))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.88), rgba(250,248,251,0.72))',
            boxShadow:
              '0 1px 2px rgba(74,60,90,0.04), 0 6px 16px -6px rgba(74,60,90,0.08), 0 20px 40px -20px rgba(74,60,90,0.10)',
          };
          return (
            <Card key={s.key} id={s.key} className={cardCls} style={cardStyle} data-testid={`today-${s.key}`}>
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <h3 className="font-serif text-xl text-[#2D2833] flex items-center gap-2"><Icon size={18} className={isProminent ? 'text-[#8A3A3A]' : 'text-[#685B72]'} /> {s.label}</h3>
                  <div className="text-xs text-[#685B72] mt-1">{s.desc} · <span className="uppercase tracking-widest text-[10px] font-semibold">{s.owner}</span></div>
                </div>
                <span className={`font-serif text-2xl ${isProminent ? 'text-[#8A3A3A]' : 'text-[#685B72]'}`}>{items.length}</span>
              </div>
              {items.length === 0 ? (
                <EmptyTile sectionKey={s.key} />
              ) : (
                <ul className="divide-y divide-[#E8E5EC] overflow-y-auto flex-1 min-h-0 -mr-2 pr-2">
                  {items.slice(0, 8).map((c, idx) => {
                    // Inquiries source rows don't have a client_id — they
                    // route to the /inquiries triage view via inquiry_id.
                    const isInquiry = s.sourceCollection === 'inquiries';
                    const rowKey = isInquiry
                      ? `inq-${c.inquiry_id || idx}`
                      : `${c.client_id || 'row'}-${idx}`;
                    const openRow = () => {
                      if (isInquiry) navigate(`/inquiries?highlight=${encodeURIComponent(c.inquiry_id || '')}`);
                      else openClient(c.client_id);
                    };
                    // Ready-for-Therapy displays the "Business Days Since
                    // Completion" counter instead of generic days-waiting.
                    const daysValue = s.key === 'ready_for_therapy'
                      ? c.days_since_completion
                      : (s.key === 'eval_overdue' ? c.days_overdue : c.days_waiting);
                    return (
                    <li key={rowKey} className="flex items-stretch gap-2 py-2.5">
                      <button
                        onClick={openRow}
                        className="flex-1 min-w-0 flex items-center justify-between gap-3 hover:bg-[#FAF8FB] rounded-md px-2 transition-colors text-left"
                        data-testid={isInquiry ? `inquiry-btn-${c.inquiry_id}` : `client-btn-${c.client_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[#2D2833] truncate">{c.client_name}</div>
                          <div className="text-[11px] text-[#685B72] truncate">
                            {isInquiry
                              ? <>
                                  {c.clinic || <span className="italic">Unassigned</span>}
                                  {c.form_submitter ? ` · Submitter: ${c.form_submitter}` : ''}
                                  {c.referral_source ? ` · ${c.referral_source}` : ''}
                                </>
                              : <>
                                  {c.clinic} · {c.service_type || '—'}
                                  {s.key === 'on_hold' && c.hold_reason ? ` · ${c.hold_reason}` : ''}
                                </>}
                          </div>
                          {/* Eval Scheduled → surface eval date + evaluator,
                              plus location + address, so the clinic team knows
                              who is coming in and when. Only rendered on
                              clinic tabs (this tile is clinicOnly). */}
                          {s.key === 'eval_scheduled' && (
                            <>
                              <div className="text-[11px] text-[#685B72] mt-0.5 font-medium" data-testid={`eval-date-${c.client_id}`}>
                                <span className="uppercase tracking-widest text-[9px] text-[#685B72] font-semibold mr-1.5">Eval</span>
                                {fmtDate(c.eval_date)}
                                {c.evaluator && (
                                  <span className="text-[#685B72] font-normal"> · {c.evaluator}</span>
                                )}
                              </div>
                              {(c.eval_location || c.location_address) && (
                                <div className="text-[11px] text-[#685B72] mt-0.5 flex items-start gap-1" data-testid={`eval-loc-${c.client_id}`}>
                                  <MapPin size={11} className="mt-0.5 shrink-0 text-[#685B72]/70" />
                                  <span className="truncate">
                                    <span className="font-semibold text-[#685B72]">{c.eval_location || 'In Clinic'}</span>
                                    {c.location_address && <span> · {c.location_address}</span>}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {s.key === 'ready_for_therapy' && typeof c.days_since_completion === 'number' ? (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#839E97]/20 text-[#839E97] font-semibold whitespace-nowrap"
                              title="Business days since eval completed"
                              data-testid={`bd-since-completion-${c.client_id}`}
                            >
                              {c.days_since_completion}bd since eval
                            </span>
                          ) : s.key === 'eval_scheduled' ? (
                            // Eval Scheduled — no "business days" badge per
                            // iter 47 (Davinder). The eval date + evaluator
                            // are rendered inline above.
                            null
                          ) : (
                            <DaysBadge days={daysValue} showZero={s.key === 'new_referrals'} />
                          )}
                          {s.key === 'paperwork_followup' && typeof c.pw_followups === 'number' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#B08A3A]/20 text-[#B08A3A] font-semibold">{c.pw_followups}/3</span>
                          )}
                          {c.urgency && s.key !== 'waitlisted' && s.key !== 'new_referrals' && <UrgencyBadge urgency={c.urgency} />}
                          {isInquiry
                            ? <span className="stage-badge bg-[#F7EFDA] text-[#B08A3A] border border-[#B08A3A]/40">NEW INQUIRY</span>
                            : <span className={`stage-badge ${stageClass(c.stage)}`}>{c.stage || '—'}</span>}
                        </div>
                      </button>
                      {s.key === 'paperwork_stalled' && (
                        <div className="flex items-center pr-2">
                          <DisposeButton client={c} onDone={load} />
                        </div>
                      )}
                      {/* Quick-action arrow (iter 47) — deep-links to the
                          section of the client modal most relevant to this
                          bucket, so the OD doesn't have to hunt for it.
                          For inquiries this deep-links to the triage view. */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isInquiry) navigate(`/inquiries?highlight=${encodeURIComponent(c.inquiry_id || '')}`);
                          else openClient(c.client_id, SECTION_FOCUS[s.key] || '');
                        }}
                        title={isInquiry ? 'Open in Inquiries triage' : `Go to ${SECTION_FOCUS[s.key] || 'client'} section`}
                        data-testid={isInquiry ? `quick-action-${s.key}-${c.inquiry_id}` : `quick-action-${s.key}-${c.client_id}`}
                        className="shrink-0 flex items-center justify-center h-8 w-8 rounded-lg text-[#685B72] hover:bg-[#685B72] hover:text-white transition-colors mr-1"
                      >
                        <ExternalLink size={13} />
                      </button>
                    </li>
                    );
                  })}
                </ul>
              )}
              {items.length > 0 && s.pipelineStage && !isInquiryBucket(s) && (
                <div className="mt-3 pt-3 border-t border-[#E8E5EC] flex items-center justify-between">
                  <span className="text-xs text-[#685B72]">
                    {items.length > 8 ? `+${items.length - 8} more not shown` : `${items.length} shown`}
                  </span>
                  <Link
                    to={`/pipeline?stage=${encodeURIComponent(s.pipelineStage)}${scopedClinic ? `&clinic=${encodeURIComponent(scopedClinic)}` : ''}`}
                    className="text-xs text-[#685B72] hover:text-[#52475A] font-semibold flex items-center gap-1"
                    data-testid={`view-all-${s.key}`}
                  >
                    View all {items.length} in Pipeline <ExternalLink size={11} />
                  </Link>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <ClientFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        client={selected}
        setup={setup}
        onSave={saveClient}
        onDelete={deleteClient}
        onReactivated={load}
        readOnly={false}
        focusSection={focusSection}
      />
    </div>
  );
}
